import { DensityUnits } from "@/libs/units/density";
import { GasFlowRateUnits, LiquidFlowRateUnits } from "@/libs/units/flowRate";
import { GenericUnits } from "@/libs/units/generic";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { PressureUnits } from "@/libs/units/pressure";
import { ProductivityIndexUnits } from "@/libs/units/productivityIndex";
import { LiquidRatioUnits } from "@/libs/units/liquidRatio";
import { ViscosityUnits } from "@/libs/units/viscosity";
import { EquipmentCalculatorSectionKey } from "../../calculationTypes";
import type { Section, SectionDeps } from "../types";
import { ScenarioDetailSectionKey, ScenarioDetailSectionTitle } from "../types";
import {
  getHpsOilGravityLabel,
  getHpsSingleLiquidRateLabel,
  getHpsTotalLiquidRateLabel,
  getHpsWaterGravityLabel
} from "@/utils/hpsLiquidType";

export const buildFluidPropertiesSideCalculatorSection = ({
  scenarioCalculations,
  wellProperties
}: SectionDeps): Section => {
  const isHpsSizing = !scenarioCalculations.isScenarioEspShore.value;
  const hpsLiquidType = wellProperties.value?.hps_liquid_type;

  const standardDisplays: Section["aggregateDisplays"] = [
    {
      label: getHpsOilGravityLabel(hpsLiquidType),
      base: DensityUnits.API,
      model: DensityUnits.options([
        DensityUnits.KilogramsPerCubicMeter,
        DensityUnits.GasPoundsPerCubicFoot
      ]),
      value: scenarioCalculations.basicData.value.oilAPI,
      mode: "convert"
    },
    {
      label: getHpsWaterGravityLabel(hpsLiquidType),
      base: DensityUnits.SpecificGravity,
      model: DensityUnits.options([
        DensityUnits.API,
        DensityUnits.GasPoundsPerCubicFoot
      ]),
      value: scenarioCalculations.basicData.value.waterSpecificGravity,
      mode: "convert"
    },
    {
      label: "Gas Gravity",
      base: DensityUnits.SpecificGravity,
      model: DensityUnits.options([
        DensityUnits.API,
        DensityUnits.KilogramsPerCubicMeter,
        DensityUnits.PoundsPerCubicFoot
      ]),
      value: scenarioCalculations.basicData.value.gasSpecificGravity,
      mode: "convert"
    },
    {
      label: "Bubble Point Pressure",
      base: PressureUnits.PoundsPerSquareInchAbsolute,
      model: PressureUnits.options(),
      value: scenarioCalculations.selectedBubblePointPressurePSIA.value,
      mode: "convert"
    },
    {
      label: "Fluid Viscosity",
      base: ViscosityUnits.Centipoise,
      model: ViscosityUnits.options(),
      value: scenarioCalculations.selectedFluidViscosityCP.value,
      mode: "convert"
    },
    {
      label: "Water Viscosity",
      base: ViscosityUnits.Centipoise,
      model: ViscosityUnits.options(),
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtPumpIntake.value
          .waterViscosityCP,
      mode: "convert"
    },
    {
      label: "Live Oil Viscosity",
      base: ViscosityUnits.Centipoise,
      model: ViscosityUnits.options(),
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtPumpIntake.value
          .oilViscosityCP,
      mode: "convert"
    },
    {
      label: "Dead Oil Viscosity",
      base: ViscosityUnits.Centipoise,
      model: ViscosityUnits.options(),
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtPumpIntake.value
          .deadOilViscosityCP,
      mode: "convert"
    },
    {
      label: "Oil Volume Factor",
      base: GenericUnits.BarrelPerStockTankBarrel,
      model: [GenericUnits.BarrelPerStockTankBarrel],
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtPumpIntake.value
          .oilVolumeFactorBBLSTB,
      mode: "read"
    },
    {
      label: "Water Volume Factor",
      base: GenericUnits.BarrelPerStockTankBarrel,
      model: [GenericUnits.BarrelPerStockTankBarrel],
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtPumpIntake.value
          .waterVolumeFactorBBLSTB,
      mode: "read"
    },
    {
      label: "Gas Viscosity",
      base: ViscosityUnits.Centipoise,
      model: ViscosityUnits.options(),
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtIntake.value
          .gasViscosityCP,
      mode: "convert"
    },
    {
      label: "Gas Density",
      base: DensityUnits.PoundsPerCubicFoot,
      model: DensityUnits.options(),
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtIntake.value
          .gasDensityLBMFT3,
      mode: "convert"
    },
    {
      label: "Gas Compressibility",
      base: GenericUnits.Generic,
      model: [GenericUnits.Generic],
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtIntake.value
          .gasCompressibilityFactor,
      mode: "read"
    },
    {
      label: "Gas Volume Factor",
      base: GenericUnits.CubicFeetPerStandardCubicFeet,
      model: [GenericUnits.CubicFeetPerStandardCubicFeet],
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtPumpIntake.value
          .gasVolumeFactorCFSFCF,
      mode: "read"
    },
    {
      label: "Gas State",
      base: GenericUnits.Generic,
      model: [GenericUnits.Generic],
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtPumpIntake.value
          .gasState,
      mode: "read"
    },
    {
      label: "Rso @ Pb",
      base: LiquidRatioUnits.StandardCubicFootPerStockTankBarrel,
      model: LiquidRatioUnits.options(),
      value:
        scenarioCalculations.fluidProperties
          .oilPhaseGasSolubilityAtBubblePointSCFSTB.value,
      mode: "convert"
    }
  ];

  const hpsDisplays: Section["aggregateDisplays"] = [
    {
      label: getHpsTotalLiquidRateLabel(hpsLiquidType),
      base: LiquidFlowRateUnits.BarrelsPerDay,
      model: LiquidFlowRateUnits.options(),
      value: scenarioCalculations.wellTestFlowRates.value.liquidFlowRateSTBD,
      mode: "convert"
    },
    {
      label: "Productivity Index",
      base: ProductivityIndexUnits.BarrelDayPsi,
      model: ProductivityIndexUnits.options(),
      value: scenarioCalculations.productivityIndexStbdPsia.value,
      mode: "convert"
    },
    {
      label: "Oil Rate",
      base: LiquidFlowRateUnits.BarrelsPerDay,
      model: LiquidFlowRateUnits.options(),
      value: wellProperties.value?.well_test_oil_rate_stb_day,
      mode: "convert"
    },
    {
      label: getHpsSingleLiquidRateLabel(hpsLiquidType),
      base: LiquidFlowRateUnits.BarrelsPerDay,
      model: LiquidFlowRateUnits.options(),
      value: wellProperties.value?.well_test_water_rate_stb_day,
      mode: "convert"
    },
    {
      label: "Gas Rate",
      base: GasFlowRateUnits.ThousandCubicFeetPerDay,
      model: [GasFlowRateUnits.ThousandCubicFeetPerDay],
      value: wellProperties.value?.well_test_gas_rate_mscf_day,
      mode: "read"
    },
    {
      label: "GOR",
      base: LiquidRatioUnits.StandardCubicFootPerStockTankBarrel,
      model: LiquidRatioUnits.options(),
      value: scenarioCalculations.wellTestFlowRates.value.gasOilRatioSCFSTB,
      mode: "read"
    },
    {
      label: "GLR",
      base: LiquidRatioUnits.StandardCubicFootPerStockTankBarrel,
      model: LiquidRatioUnits.options(),
      value: scenarioCalculations.wellTestFlowRates.value.gasLiquidRatioSCFSTB,
      mode: "read"
    },
    {
      label: "Datum Point",
      base: LengthUnits.Feet,
      model: LargeLengthUnits.options(),
      value: wellProperties.value?.well_test_datum_point_measured_depth_feet,
      mode: "convert"
    },
    {
      label: "SBHP",
      base: PressureUnits.PoundsPerSquareInchAbsolute,
      model: PressureUnits.options(),
      value: wellProperties.value?.well_test_static_bottom_hole_pressure_psia,
      mode: "convert"
    },
    {
      label: "PBHP",
      base: PressureUnits.PoundsPerSquareInchAbsolute,
      model: PressureUnits.options(),
      value:
        wellProperties.value?.well_test_producing_bottom_hole_pressure_psia,
      mode: "convert"
    }
  ];

  return {
    id: EquipmentCalculatorSectionKey.FLUID_PROPERTIES,
    hash: ScenarioDetailSectionKey.FLUID_PROPERTIES,
    label: ScenarioDetailSectionTitle.FLUID_PROPERTIES,
    routeName: "WellInfo",
    aggregateDisplays: isHpsSizing ? hpsDisplays : standardDisplays
  };
};
